import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AdminGridDirective, AdminGridConfig, AdminGridCallbackEvent } from '../../shared/admin-grid.directive';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';
import { TemplateTypeEditComponent } from './template-type-edit/template-type-edit.component';
import { TemplateEditComponent } from './template-edit/template-edit.component';

/**
 * Angular 12 migration of admin.templates-list / admin.templates-type-list.
 * Two modes controlled by route data { typeList: true }:
 *   - templates-list:      action_code=7w37qb, grid_id=72
 *   - templates-type-list: action_code=12jct6, grid_id=73
 */
@Component({
    selector: 'gtw-templates',
    templateUrl: './templates.component.html',
    styleUrls: ['./templates.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TemplatesComponent implements OnInit, OnDestroy {
  @ViewChild(AdminGridDirective) adminGrid!: AdminGridDirective;

  gridConfig!: AdminGridConfig;
  loading = false;
  isTypeList = false;

  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    this.isTypeList = !!this.route.snapshot.data['typeList'];
    this.buildGridConfig();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridCallback(event: AdminGridCallbackEvent): void {
    const rowData = event.data?.rowData ?? event.data;
    if (this.isTypeList) {
      this.openTemplateTypeModal(rowData);
    } else {
      this.openTemplateModal(rowData);
    }
  }

  onGridDataLoaded(_data: any): void {
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh();
  }

  addTemplate(): void {
    this.openTemplateModal(null);
  }

  addTemplateType(): void {
    this.openTemplateTypeModal(null);
  }

  navigateToTypeList(): void {
    this.router.navigate(['/admin', 'templates-type-list']);
  }

  navigateToTemplatesList(): void {
    this.router.navigate(['/admin', 'templates-list']);
  }

  private openTemplateTypeModal(rowData: any): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '600px';
    config.maxWidth = '90vw';
    config.disableClose = true;
    config.data = {
      rowData: rowData || {},
      mode: rowData?.TEMPLATE_TYPE_KEY ? 'edit' : 'new',
    };

    this.adminScreenContext.setRowData(rowData || {});
    const ref = this.dialog.open(TemplateTypeEditComponent, config);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.adminScreenContext.setRowData({});
        if (result === 'saved') {
          this.adminGrid?.clearCache();
          this.adminGrid?.refresh();
        }
      });
  }

  private openTemplateModal(rowData: any): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '90vw';
    config.disableClose = true;
    config.data = {
      rowData: rowData || {},
      mode: rowData?.TEMPLATE_KEY ? 'edit' : 'new',
    };

    this.adminScreenContext.setRowData(rowData || {});
    const ref = this.dialog.open(TemplateEditComponent, config);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.adminScreenContext.setRowData({});
        if (result === 'saved') {
          this.adminGrid?.clearCache();
          this.adminGrid?.refresh();
        }
      });
  }

  private buildGridConfig(): void {
    if (this.isTypeList) {
      this.gridConfig = {
        actionCode: '12jct6',
        gridId: '73',
        autoLoad: true,
        cacheKey: 'admin/templates-type-list',
      };
    } else {
      this.gridConfig = {
        actionCode: '7w37qb',
        gridId: '72',
        autoLoad: true,
        cacheKey: 'admin/templates-list',
      };
    }
  }
}
